import { useState, useEffect, useMemo } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  Calendar,
  Plus,
  Settings,
  Trash2,
  Edit,
  Clock,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Eye,
  Building2,
  Play,
  RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLocation } from "wouter";

export default function ConsultaNFAgendada() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  
  const utils = trpc.useUtils();

  // Estados de filtro
  const [busca, setBusca] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  // Estados de modal
  const [showAgendamentosModal, setShowAgendamentosModal] = useState(false);
  const [showNovoAgendamentoModal, setShowNovoAgendamentoModal] = useState(false);
  const [showEditarAgendamentoModal, setShowEditarAgendamentoModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [agendamentoParaEditar, setAgendamentoParaEditar] = useState<any>(null);
  const [execucaoParaLogs, setExecucaoParaLogs] = useState<number | null>(null);

  // Estados do formulário de agendamento
  const [novoAgendamento, setNovoAgendamento] = useState({
    emitenteId: 0,
    certificadoId: 0,
    tipoConsulta: "emissor" as "emissor" | "tomador",
    horarioExecucao: "08:00",
  });

  const { data: execucoes = [], isLoading: loadingExecucoes } = trpc.consultaNf.execucoes.list.useQuery({
    busca: busca || undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
    limit: 100,
  });

  const { data: agendamentos = [], isLoading: loadingAgendamentos } = trpc.consultaNf.agendamentos.list.useQuery();

  const { data: emitentesComCertificado = [] } = trpc.consultaNf.emitentesComCertificado.useQuery();

  const { data: logs = [] } = trpc.consultaNf.execucoes.logs.useQuery(
    { execucaoId: execucaoParaLogs! },
    { enabled: !!execucaoParaLogs }
  );

  // Mutations
  const createAgendamento = trpc.consultaNf.agendamentos.create.useMutation({
    onSuccess: () => {
      toast.success("Agendamento criado com sucesso");
      utils.consultaNf.agendamentos.list.invalidate();
      setShowNovoAgendamentoModal(false);
      resetNovoAgendamento();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar agendamento");
    },
  });

  const updateAgendamento = trpc.consultaNf.agendamentos.update.useMutation({
    onSuccess: () => {
      toast.success("Agendamento atualizado com sucesso");
      utils.consultaNf.agendamentos.list.invalidate();
      setShowEditarAgendamentoModal(false);
      setAgendamentoParaEditar(null);
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar agendamento");
    },
  });

  const deleteAgendamento = trpc.consultaNf.agendamentos.delete.useMutation({
    onSuccess: () => {
      toast.success("Agendamento excluído com sucesso");
      utils.consultaNf.agendamentos.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir agendamento");
    },
  });

  const executarAgora = trpc.consultaNf.agendamentos.executarAgora.useMutation({
    onSuccess: (data) => {
      toast.success(data.message || "Consulta executada com sucesso");
      utils.consultaNf.execucoes.list.invalidate();
      utils.consultaNf.agendamentos.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao executar consulta");
    },
  });

  const resetarNSU = trpc.consultaNf.agendamentos.resetarNSU.useMutation({
    onSuccess: () => {
      toast.success("NSU resetado! Pr\u00f3xima consulta come\u00e7ar\u00e1 do zero");
      utils.consultaNf.agendamentos.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao resetar NSU");
    },
  });

  // Contar agendamentos por emitente
  const agendamentosPorEmitente = useMemo(() => {
    const counts: Record<number, number> = {};
    agendamentos.forEach((a: any) => {
      counts[a.emitenteId] = (counts[a.emitenteId] || 0) + 1;
    });
    return counts;
  }, [agendamentos]);

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }


  // Funções auxiliares
  const resetNovoAgendamento = () => {
    setNovoAgendamento({
      emitenteId: 0,
      certificadoId: 0,
      tipoConsulta: "emissor",
      horarioExecucao: "08:00",
    });
  };

  const handleSelectEmitente = (emitenteId: string) => {
    const emitente = emitentesComCertificado.find((e: any) => e.id === parseInt(emitenteId));
    if (emitente) {
      setNovoAgendamento({
        ...novoAgendamento,
        emitenteId: emitente.id,
        certificadoId: emitente.certificadoId,
      });
    }
  };

  const handleCriarAgendamento = () => {
    if (!novoAgendamento.emitenteId || !novoAgendamento.certificadoId) {
      toast.error("Selecione um emitente");
      return;
    }
    createAgendamento.mutate(novoAgendamento);
  };

  const handleEditarAgendamento = () => {
    if (!agendamentoParaEditar) return;
    updateAgendamento.mutate({
      id: agendamentoParaEditar.id,
      tipoConsulta: agendamentoParaEditar.tipoConsulta,
      horarioExecucao: agendamentoParaEditar.horarioExecucao,
    });
  };

  const handleExcluirAgendamento = (id: number) => {
    if (confirm("Tem certeza que deseja excluir este agendamento?")) {
      deleteAgendamento.mutate({ id });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sucesso":
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" /> Sucesso</Badge>;
      case "falha":
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Falha</Badge>;
      case "parcial":
        return <Badge className="bg-yellow-500"><AlertCircle className="w-3 h-3 mr-1" /> Parcial</Badge>;
      case "em_andamento":
        return <Badge className="bg-blue-500"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Em andamento</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return "-";
    const d = date instanceof Date ? date : new Date(date);
    return format(d, "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const formatCnpj = (cnpj: string) => {
    if (!cnpj) return "-";
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-500" />
            Consulta NF Agendada
          </h1>
          <p className="text-muted-foreground">
            Gerencie consultas automáticas de notas fiscais na SEFAZ
          </p>
        </div>
        <Button onClick={() => setShowAgendamentosModal(true)} className="bg-orange-500 hover:bg-orange-600">
          <Settings className="w-4 h-4 mr-2" />
          Agendamentos
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Busca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por CNPJ ou emitente..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Label>Data Início</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Label>Data Fim</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setBusca("");
                setDataInicio("");
                setDataFim("");
              }}
            >
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Histórico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Consultas</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingExecucoes ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : execucoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma consulta realizada ainda
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Emitente</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Requisições</TableHead>
                  <TableHead>Documentos</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {execucoes.map((exec: any) => (
                  <TableRow key={exec.id}>
                    <TableCell className="font-mono text-sm">{formatCnpj(exec.cnpj)}</TableCell>
                    <TableCell>{exec.emitenteNome || "-"}</TableCell>
                    <TableCell>{formatDate(exec.dataHoraExecucao)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {exec.tipoConsulta === "emissor" ? "Emissor" : "Tomador"}
                      </Badge>
                    </TableCell>
                    <TableCell>{exec.totalRequisicoes || 0}</TableCell>
                    <TableCell>{exec.totalDocumentos || 0}</TableCell>
                    <TableCell>{getStatusBadge(exec.status)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setExecucaoParaLogs(exec.id);
                          setShowLogsModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Gerenciamento de Agendamentos */}
      <Dialog open={showAgendamentosModal} onOpenChange={setShowAgendamentosModal}>
        <DialogContent className="!w-[95vw] !max-w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Agendamentos Configurados
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowNovoAgendamentoModal(true)} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" />
                Novo Agendamento
              </Button>
            </div>

            {loadingAgendamentos ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : agendamentos.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum agendamento configurado
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Emitente</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Horário</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Última Execução</TableHead>
                    <TableHead>Próxima Execução</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agendamentos.map((ag: any) => (
                    <TableRow key={ag.id}>
                      <TableCell>{ag.emitenteNome}</TableCell>
                      <TableCell className="font-mono text-sm">{formatCnpj(ag.emitenteCnpj)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {ag.tipoConsulta === "emissor" ? "Emissor" : "Tomador"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          {ag.horarioExecucao}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {ag.ativo ? (
                            <Badge className="bg-green-500">Ativo</Badge>
                          ) : (
                            <Badge variant="secondary">Inativo</Badge>
                          )}
                          {ag.emExecucao && (
                            <Badge className="bg-blue-500" title={`Iniciado em ${ag.execucaoIniciadaEm ? new Date(ag.execucaoIniciadaEm).toLocaleString('pt-BR') : ''}`}>
                              Em Execução
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{ag.ultimaExecucao ? formatDate(ag.ultimaExecucao) : "-"}</TableCell>
                      <TableCell>{ag.proximaExecucao ? formatDate(ag.proximaExecucao) : "-"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAgendamentoParaEditar(ag);
                              setShowEditarAgendamentoModal(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExcluirAgendamento(ag.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm("Executar consulta agora?")) {
                                executarAgora.mutate({ id: ag.id });
                              }
                            }}
                            disabled={executarAgora.isPending || !ag.ativo || ag.emExecucao}
                            title={ag.emExecucao ? "Consulta já em execução" : "Executar agora"}
                          >
                            {executarAgora.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm("Resetar NSU? Pr\u00f3xima consulta come\u00e7ar\u00e1 do zero.")) {
                                resetarNSU.mutate({ id: ag.id });
                              }
                            }}
                            disabled={resetarNSU.isPending || ag.emExecucao}
                            title="Resetar NSU (consultar do zero)"
                          >
                            {resetarNSU.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4 text-blue-500" />
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Novo Agendamento */}
      <Dialog open={showNovoAgendamentoModal} onOpenChange={setShowNovoAgendamentoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Novo Agendamento
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Emitente *</Label>
              <Select
                value={novoAgendamento.emitenteId ? String(novoAgendamento.emitenteId) : ""}
                onValueChange={handleSelectEmitente}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um emitente com certificado" />
                </SelectTrigger>
                <SelectContent>
                  {emitentesComCertificado.map((emitente: any) => {
                    const count = agendamentosPorEmitente[emitente.id] || 0;
                    const disabled = count >= 2;
                    return (
                      <SelectItem key={emitente.id} value={String(emitente.id)} disabled={disabled}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{emitente.razaoSocial}</span>
                          <span className="text-muted-foreground text-xs">
                            ({formatCnpj(emitente.cnpj)})
                          </span>
                          {disabled && <Badge variant="secondary" className="ml-2">Max 2</Badge>}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {emitentesComCertificado.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Nenhum emitente com certificado digital cadastrado
                </p>
              )}
            </div>

            <div>
              <Label>Tipo de Consulta *</Label>
              <Select
                value={novoAgendamento.tipoConsulta}
                onValueChange={(v) => setNovoAgendamento({ ...novoAgendamento, tipoConsulta: v as "emissor" | "tomador" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="emissor">Emissor (notas emitidas)</SelectItem>
                  <SelectItem value="tomador">Tomador (notas recebidas)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Horário de Execução *</Label>
              <Input
                type="time"
                value={novoAgendamento.horarioExecucao}
                onChange={(e) => setNovoAgendamento({ ...novoAgendamento, horarioExecucao: e.target.value })}
              />
              <p className="text-sm text-muted-foreground mt-1">
                A consulta será executada diariamente neste horário
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoAgendamentoModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCriarAgendamento}
              disabled={createAgendamento.isPending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {createAgendamento.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Criar Agendamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Agendamento */}
      <Dialog open={showEditarAgendamentoModal} onOpenChange={setShowEditarAgendamentoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Editar Agendamento
            </DialogTitle>
          </DialogHeader>

          {agendamentoParaEditar && (
            <div className="space-y-4">
              <div>
                <Label>Emitente</Label>
                <Input value={agendamentoParaEditar.emitenteNome} disabled />
                <p className="text-sm text-muted-foreground mt-1">
                  O emitente não pode ser alterado. Para mudar, exclua e crie um novo agendamento.
                </p>
              </div>

              <div>
                <Label>Tipo de Consulta *</Label>
                <Select
                  value={agendamentoParaEditar.tipoConsulta}
                  onValueChange={(v) => setAgendamentoParaEditar({ ...agendamentoParaEditar, tipoConsulta: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emissor">Emissor (notas emitidas)</SelectItem>
                    <SelectItem value="tomador">Tomador (notas recebidas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Horário de Execução *</Label>
                <Input
                  type="time"
                  value={agendamentoParaEditar.horarioExecucao}
                  onChange={(e) => setAgendamentoParaEditar({ ...agendamentoParaEditar, horarioExecucao: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarAgendamentoModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleEditarAgendamento}
              disabled={updateAgendamento.isPending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {updateAgendamento.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Logs */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Logs da Execução
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum log encontrado
              </div>
            ) : (
              logs.map((log: any) => (
                <div
                  key={log.id}
                  className={`p-3 rounded-lg border ${
                    log.nivel === "error"
                      ? "border-red-500 bg-red-50 dark:bg-red-950"
                      : log.nivel === "warning"
                      ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-950"
                      : "border-gray-200 bg-gray-50 dark:bg-gray-900"
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm">
                    {log.nivel === "error" && <XCircle className="w-4 h-4 text-red-500" />}
                    {log.nivel === "warning" && <AlertCircle className="w-4 h-4 text-yellow-500" />}
                    {log.nivel === "info" && <CheckCircle className="w-4 h-4 text-green-500" />}
                    <span className="text-muted-foreground">{formatDate(log.criadoEm)}</span>
                  </div>
                  <p className="mt-1">{log.mensagem}</p>
                  {log.detalhes && (
                    <pre className="mt-2 text-xs bg-black/10 p-2 rounded overflow-x-auto">
                      {JSON.stringify(typeof log.detalhes === 'string' ? JSON.parse(log.detalhes) : log.detalhes, null, 2)}
                    </pre>
                  )}
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLogsModal(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
